import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

import { CartProvider } from './context/CartContext';
import ErrorBoundary from './components/ErrorBoundary';
import AnnouncementBar from './components/layout/AnnouncementBar';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import CartDrawer from './components/cart/CartDrawer';
import { usePageView } from './hooks/usePageView';

import HomePage from './pages/HomePage';
import FragrancePage from './pages/FragrancePage';
import ArtPage from './pages/ArtPage';
import BodyPage from './pages/BodyPage';
import ProductPage from './pages/ProductPage';
import ArtworkProductPage from './pages/ArtworkProductPage';
import MusicPage from './pages/MusicPage';
import AboutPage from './pages/AboutPage';
import CheckoutPage from './pages/CheckoutPage';
import OrderConfirmationPage from './pages/OrderConfirmationPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import ShippingPage from './pages/ShippingPage';
import ReturnsPage from './pages/ReturnsPage';
import EventsPage from './pages/EventsPage';

import './styles/global.css';
import './App.css';

/** Tracks page views on every route change. Placed once inside BrowserRouter. */
const PageViewTracker: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  usePageView();
  return <>{children}</>;
};

const App: React.FC = () => (
  <ErrorBoundary>
    <BrowserRouter>
      <PageViewTracker>
      <CartProvider>
        <div className="app">
          <AnnouncementBar
            message="New fragrance just released — Vinyl Rose."
            link={{ href: '/fragrance/vinyl-rose-perfume-oil', label: 'Shop now' }}
          />

          <Header />

          {/* Cart drawer — lives here so it's available everywhere */}
          <CartDrawer />

          <div className="app__content">
            <Routes>
              <Route path="/" element={<HomePage />} />

              <Route path="/art" element={<ArtPage />} />
              <Route path="/art/:handle" element={<ArtworkProductPage />} />

              <Route path="/fragrance" element={<FragrancePage />} />
              <Route path="/fragrance/:handle" element={<ProductPage category="fragrance" />} />

              <Route path="/body" element={<BodyPage />} />
              <Route path="/body/:handle" element={<ProductPage category="body" />} />

              <Route path="/music" element={<MusicPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/order-confirmation" element={<OrderConfirmationPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/policies/shipping" element={<ShippingPage />} />
              <Route path="/policies/returns" element={<ReturnsPage />} />

              {/* Stubs */}
              <Route path="/events" element={<EventsPage />} />
              <Route path="/search" element={<Navigate to="/" replace />} />
              <Route path="/account" element={<Navigate to="/" replace />} />
              <Route path="/cart" element={<Navigate to="/" replace />} />
              <Route path="/contact" element={<Navigate to="/about" replace />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>

          <Footer />
        </div>
      </CartProvider>
      </PageViewTracker>
    </BrowserRouter>
  </ErrorBoundary>
);

export default App;
